import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/services/db/supabase';
import { logPlatformAccess } from '@/services/db/auditLog';
import { useAuthStore } from '@/store/authStore';
import type { UserRole } from '@/store/authStore';
import { getProjectsForInvite } from '@/services/db/adminProjects';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const INVITABLE_ROLES: { value: UserRole; label: string }[] = [
  { value: 'ssr', label: 'SSR' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'safety_manager', label: 'Safety Manager' },
  { value: 'executive', label: 'Executive' },
  { value: 'viewer', label: 'Viewer' },
];

const SCOPE_TYPES = [
  { value: 'business_unit', label: 'Business Unit' },
  { value: 'division', label: 'Division' },
] as const;

// Conditional schema: SSR needs projectId; every other role needs scopeType+scopeId.
const inviteSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z.string().email('Enter a valid email address'),
    role: z.string().min(1, 'Role is required'),
    scopeType: z.enum(['business_unit', 'division']).optional(),
    scopeId: z.string().optional(),
    projectId: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.role === 'ssr') {
      if (!val.projectId) {
        ctx.addIssue({
          code: 'custom',
          path: ['projectId'],
          message: 'Assigned project is required for SSR',
        });
      }
    } else if (val.role) {
      if (!val.scopeType) {
        ctx.addIssue({
          code: 'custom',
          path: ['scopeType'],
          message: 'Scope type is required',
        });
      }
      if (!val.scopeId) {
        ctx.addIssue({
          code: 'custom',
          path: ['scopeId'],
          message: 'Scope is required',
        });
      }
    }
  });

type InviteForm = z.infer<typeof inviteSchema>;

interface ScopeOption {
  id: string;
  name: string;
}

export default function UsersSection() {
  const { userRole, tenantId, session } = useAuthStore();
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [scopeOptions, setScopeOptions] = useState<ScopeOption[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InviteForm>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      scopeType: undefined,
      scopeId: '',
      projectId: '',
    },
  });

  const selectedRole = watch('role');
  const selectedScopeType = watch('scopeType');
  const isSSR = selectedRole === 'ssr';

  // Fetch scope options when scope type changes (non-SSR flow)
  useEffect(() => {
    if (!selectedScopeType || !tenantId || isSSR) {
      setScopeOptions([]);
      return;
    }
    const table =
      selectedScopeType === 'business_unit' ? 'business_units' : 'divisions';
    supabase
      .from(table)
      .select('id, name')
      .eq('tenant_id', tenantId)
      .eq('is_active', true)
      .eq('is_deleted', false)
      .order('name')
      .then(({ data }) => {
        setScopeOptions(data ?? []);
      });
    setValue('scopeId', '');
  }, [selectedScopeType, tenantId, setValue, isSSR]);

  // Projects for SSR picker
  const { data: projectsForInvite } = useQuery({
    queryKey: ['admin-invite-projects', tenantId],
    queryFn: () => getProjectsForInvite(tenantId!),
    enabled: !!tenantId && isSSR,
  });

  // Reset unused fields on role change
  useEffect(() => {
    if (isSSR) {
      setValue('scopeType', undefined);
      setValue('scopeId', '');
    } else {
      setValue('projectId', '');
    }
  }, [isSSR, setValue]);

  const { data: users, refetch: refetchUsers } = useQuery({
    queryKey: ['admin-users', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from('users')
        .select('id, first_name, last_name, email, role, is_active, created_at')
        .eq('tenant_id', tenantId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false });
      if (error) throw error;

      if (userRole === 'platform_admin') {
        logPlatformAccess({
          tenantId,
          action: 'read',
          tableName: 'users',
          querySnapshot: `Listed ${data?.length ?? 0} users`,
        });
      }

      return data ?? [];
    },
    enabled: !!tenantId,
  });

  const onSubmit = async (data: InviteForm) => {
    setServerError(null);
    setSuccessMsg(null);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const payload: Record<string, unknown> = {
        email: data.email,
        role: data.role,
        firstName: data.firstName,
        lastName: data.lastName,
      };
      if (data.role === 'ssr') {
        payload.projectId = data.projectId;
      } else {
        payload.scopeType = data.scopeType;
        payload.scopeId = data.scopeId;
      }

      const res = await fetch(`${supabaseUrl}/functions/v1/invite-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (!res.ok) {
        setServerError(result.error || 'Failed to send invitation');
        return;
      }

      setSuccessMsg(
        result.warning
          ? `Invitation sent to ${data.email} (warning: ${result.warning})`
          : `Invitation sent to ${data.email}`,
      );
      reset();
      refetchUsers();
    } catch {
      setServerError('Failed to send invitation. Try again.');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Invite User</h2>
          <p className="text-sm text-muted-foreground">
            Send an invitation email to add a new team member.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {successMsg && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                {successMsg}
              </div>
            )}
            {serverError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {serverError}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input id="firstName" {...register('firstName')} />
                {errors.firstName && (
                  <p className="text-xs text-destructive">
                    {errors.firstName.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input id="lastName" {...register('lastName')} />
                {errors.lastName && (
                  <p className="text-xs text-destructive">
                    {errors.lastName.message}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@company.com"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-xs text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select
                  onValueChange={(v) =>
                    setValue('role', v ?? '', { shouldValidate: true })
                  }
                  value={watch('role') || undefined}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {INVITABLE_ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.role && (
                  <p className="text-xs text-destructive">
                    {errors.role.message}
                  </p>
                )}
              </div>

              {isSSR ? (
                <div className="space-y-2 sm:col-span-2">
                  <Label>Assign to Project *</Label>
                  <Select
                    onValueChange={(v) =>
                      setValue('projectId', v ?? '', { shouldValidate: true })
                    }
                    value={watch('projectId') || undefined}
                    disabled={!projectsForInvite || projectsForInvite.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          projectsForInvite && projectsForInvite.length === 0
                            ? 'No active projects — create one first'
                            : 'Select project'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {(projectsForInvite ?? []).map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                          {p.code ? ` (${p.code})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.projectId && (
                    <p className="text-xs text-destructive">
                      {errors.projectId.message}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    SSRs can only see DSRs for their assigned project.
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Scope Type *</Label>
                    <Select
                      onValueChange={(v) => {
                        if (v)
                          setValue(
                            'scopeType',
                            v as 'business_unit' | 'division',
                            { shouldValidate: true },
                          );
                      }}
                      value={selectedScopeType}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {SCOPE_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.scopeType && (
                      <p className="text-xs text-destructive">
                        {errors.scopeType.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Scope *</Label>
                    <Select
                      onValueChange={(v) =>
                        setValue('scopeId', v ?? '', { shouldValidate: true })
                      }
                      value={watch('scopeId') || undefined}
                      disabled={scopeOptions.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select scope" />
                      </SelectTrigger>
                      <SelectContent>
                        {scopeOptions.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.scopeId && (
                      <p className="text-xs text-destructive">
                        {errors.scopeId.message}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            >
              {isSubmitting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Active Users</h2>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Invited</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users && users.length > 0 ? (
                users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">
                      {u.first_name} {u.last_name}
                    </TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize text-xs">
                        {(u.role as string).replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={u.is_active ? 'default' : 'secondary'}
                        className={
                          u.is_active
                            ? 'bg-emerald-100 text-emerald-700 text-xs'
                            : 'text-xs'
                        }
                      >
                        {u.is_active ? 'Active' : 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(u.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-muted-foreground py-8"
                  >
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
